import { Graphics } from 'pixi.js';
import { Boss, type BossDef } from './boss-base';
import { getTexture } from '@/assets/resolver';
import type { BulletSystem } from '@/entities/bullets';
import { bendFriendly, deflectRing } from '@/entities/bullet-fields';
import type { TelegraphSink } from '@/render/telegraphs';

/**
 * Magnet — a dipole that alternately PULLS the ship in (red pole)
 * and SHOVES it away (blue pole), each window telegraphed by a pole flare.
 * Gentle, readable force so it teaches movement without overwhelming. The
 * force is applied via the shared environmentalForce hook. docs/02 boss pool.
 * The poles act on SHOTS too (docs/02 B1): while it pushes, a blue magnetic
 * SHELL bounces the player's bullets; while it pulls, they curve in — the
 * pull half is the window to shoot. Nova bolts curl along field lines.
 * Signatures: pull/push polarity cycle (always), star ring (P1/P2).
 */
export const MAGNET: BossDef = {
  id: 'magnet',
  nameKey: 'boss.magnet',
  tier: 2,
  minRoom: 1,
  baseHp: 1050, // the push shell halves uptime — pull bonus + lighter hull keep TTK
  radius: 46,
  bodyKey: 'boss/magnet/body',
  movement: 'hover',
  contactDamage: 1,
  phases: [
    {
      hpPct: 1.0,
      patterns: [
        {
          // Field lines: the ring curls both ways (B3 curve, alternating)
          type: 'radial', bullet: 'bullet/enemy/star', count: 10, interval: 3.0,
          spinDeg: 13, speed: 120, radius: 12, damage: 1,
          motion: { curveDeg: 22, curveAlternate: true },
        },
      ],
    },
    {
      hpPct: 0.5,
      speedMul: 1.15,
      patterns: [
        {
          type: 'radial', bullet: 'bullet/enemy/star', count: 12, interval: 2.8,
          spinDeg: -16, speed: 130, radius: 12, damage: 1,
        },
        {
          type: 'aimed', bullet: 'bullet/enemy/ring', count: 3, spreadDeg: 18,
          interval: 2.8, telegraph: 0.4, speed: 240, radius: 12, damage: 2,
          unlockTier: 1,
        },
      ],
    },
  ],
  evolve: [
    {
      // Field lines: a lazy twin-arm star spiral
      tier: 2,
      pattern: {
        type: 'spiral', bullet: 'bullet/enemy/star', arms: 2, interval: 0.24,
        angularSpeed: 50, speed: 115, radius: 11, damage: 1,
      },
    },
    {
      // Arc flash: a telegraphed wide star fan once the core is exposed
      tier: 3,
      fromPhase: 1,
      pattern: {
        type: 'aimed', bullet: 'bullet/enemy/star', count: 5, spreadDeg: 40,
        interval: 3.4, telegraph: 0.45, speed: 235, radius: 11, damage: 1,
      },
    },
  ],
};

const HALF = 4.2; // seconds per pull/push window
const WINDUP = 0.7; // pole flare before the force kicks in
const FORCE = 165; // px/s at point blank — mild, fades with distance
const RANGE = 720;
/** Shot fields: a reflective shell while pushing, an inward bend while pulling */
const SHELL = { r: 118, band: 24, width: 6 } as const;
const ATTRACT = { range: 520, pull: -900, damageMul: 1.3 } as const;
const POLE = { push: 0x4a90d9, pull: 0xff5a4a, core: 0xeaf4ff } as const;

export class MagnetBoss extends Boss {
  private timer = HALF;
  private pulling = true;
  private flare = WINDUP;
  private time = 0;
  private readonly field = new Graphics();

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(MAGNET, level, x, y, homeX, homeY);
    this.allowReinforce = false;
    this.view.addChildAt(this.field, 0);
  }

  /** The push shell bounces every shot; pulling (and the flare) leave it open. */
  private get shellUp(): boolean {
    return !this.pulling && this.flare <= 0;
  }

  override get isVulnerable(): boolean {
    return super.isVulnerable && !this.shellUp;
  }

  /** Shell up: blasts glance off too; pulling: the exposed core takes more. */
  override takeDamage(amount: number): boolean {
    if (this.shellUp) {
      this.hitFlash = 0.08;
      return false;
    }
    return super.takeDamage(this.pulling ? amount * ATTRACT.damageMul : amount);
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    super.update(dt, px, py, bullets, telegraphs);
    if (this.dead) return;
    this.time += dt;
    if (this.flare > 0) this.flare -= dt;
    this.timer -= dt;
    if (this.timer <= 0) {
      this.timer = HALF / this.intensity;
      this.pulling = !this.pulling;
      this.flare = WINDUP; // flare before the next polarity engages
    }
    if (this.shellUp) deflectRing(bullets, this.x, this.y, SHELL.r, SHELL.band);
    else if (this.pulling && this.flare <= 0) {
      bendFriendly(bullets, this.x, this.y, this.def.radius, ATTRACT.range, ATTRACT.pull, 0, dt);
    }
  }

  override environmentalForce(px: number, py: number): { x: number; y: number } | null {
    if (this.dead || this.flare > 0) return null;
    const dx = this.x - px;
    const dy = this.y - py;
    const d = Math.hypot(dx, dy);
    if (d < this.def.radius + 16 || d > RANGE) return null;
    const sign = this.pulling ? 1 : -1;
    const strength = FORCE * (1 - d / RANGE) * sign;
    return { x: (dx / d) * strength, y: (dy / d) * strength };
  }

  override render(alpha: number): void {
    super.render(alpha);
    this.body.texture = getTexture('boss/magnet/body', this.flare > 0 ? 1 : 0);
    const g = this.field;
    g.clear();
    if (this.shellUp) {
      // Blue magnetic shell — shots bounce off it
      const pulse = 1 + Math.sin(this.time * 8) * 0.03;
      g.circle(0, 0, SHELL.r * pulse).stroke({ color: POLE.push, width: SHELL.width, alpha: 0.6 });
      g.circle(0, 0, SHELL.r * pulse).stroke({ color: POLE.core, width: 1.5, alpha: 0.6 });
    } else if (this.flare > 0 && !this.pulling) {
      // The shell flickers in during the flare (telegraph)
      if (Math.sin(this.time * 40) > 0) {
        g.circle(0, 0, SHELL.r).stroke({ color: POLE.push, width: 2, alpha: 0.5 });
      }
    } else if (this.pulling) {
      // Red field lines streaming inward — shots will curve in
      for (let i = 0; i < 8; i++) {
        const a = (Math.PI * 2 * i) / 8;
        const t = 1 - ((this.time * 0.8 + i / 8) % 1);
        const r = this.def.radius + 24 + t * 150;
        g.circle(Math.cos(a) * r, Math.sin(a) * r, 3).fill({ color: POLE.pull, alpha: 0.55 * t });
      }
    }
  }
}
